import { EventListeners, MinzeElement, Reactive } from 'minze';
import { config } from '../../constants';
import { Locale } from '../../modules/locale';
import { SquidexTextLabels } from '../../modules/squidex/labels';

export interface TjNews {
  news: any[];
  page: number;
}

Locale.getInstance().registerKeySet('fr', 'news', {
  pageTitle: 'Nos nouvelles les plus récentes',
});

Locale.getInstance().registerKeySet('en', 'news', {
  pageTitle: 'Our most recent news',
});

export class TjNews extends MinzeElement {
  reactive: Reactive = [
    ['news', []],
    ['page', 1],
  ];
  lang = Locale.language;
  itemsPerPage = 12;
  newsData = [];
  isMobile = window.matchMedia('(max-width: 768px)').matches;

  public async loadNews() {
    const api = `${config.apiRoute}/aproposNews?lang=${this.lang}`;
    const response = await fetch(api);
    const data = await response.json();
    console.log(data)
    if (data && data.length > 0) {
      this.news = data
      this.newsData = data
    }
    this.rerender();
  }

  changePage = (event: Event) => {
    const dateCard = (event.target as Element)?.closest('.pagination-button') as Element | null;
    const totalPages = Math.ceil(this.news.length / this.itemsPerPage);
    const page = parseInt(dateCard?.getAttribute('data-page') as string);
    if (page && page <= totalPages) this.page = page;
  };

  renderPagination = () => {
    const renderButton = (page: number) => {
      return `
        <button class="${page === this.page ? 'active' : ''} pagination-button" data-page="${page}">
          ${page}
        </button>
      `;
    };

    const renderEllipsis = () => `<button class="pagination-button ellipsis">...</button>`;

    const totalPages = Math.ceil(this.news.length / this.itemsPerPage);
    const buttonArray = Array.from({ length: totalPages > 4 ? 5 : totalPages }, (_, index) => index + 1);

    let pagination = ``;

    if (totalPages > 5) {
      const currentPage = this.page;
      if (currentPage + 2 >= totalPages - 1) {
        pagination = pagination + renderButton(1);
        pagination = pagination + renderEllipsis();
      }
      const isCurrentPageFirst = currentPage === 0;
      const isCurrentPageLast = currentPage + 1 === totalPages;
      for (let i = 1; i <= 3; i++) {
        const page = currentPage - (isCurrentPageLast ? 2 : !isCurrentPageFirst ? 1 : 0) + i;
        if (page <= totalPages) pagination = pagination + renderButton(page);
      }
      if (currentPage + 2 < totalPages) {
        if (currentPage + 2 < totalPages - 1) pagination = pagination + renderEllipsis();
        pagination = pagination + renderButton(totalPages);
      }
    } else {
      pagination = buttonArray.map((button) => `${renderButton(button)}`).join('');
    }
    return `
      <div class="pagination">
        <button class="prev pagination-button" data-page="${this.page - 1}">
          <tj-pagination-prev-icon></tj-pagination-prev-icon>
        </button>
        ${pagination}
        <button class="next pagination-button" data-page="${this.page + 1}">
          <tj-pagination-next-icon></tj-pagination-next-icon>
        </button>
      </div>
    `;
  };

  onReady() {
    this.loadNews();
  }

  formatDate(input: string): string {
    let date = new Date(input);
    return date.toLocaleDateString(this.lang === 'en' ? 'en-EN' : 'fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  html = () => /* html */ `
    <tj-text font-weight="500" line-height="125%" font-size="34px" color="#068FC3" class="page-title">
      ${SquidexTextLabels.getKey('news.pageTitle')}
    </tj-text>
    <div class="news-grid">
      ${this.news
        .slice((this.page - 1) * this.itemsPerPage, this.itemsPerPage * this.page)
        .map(
          (item) => /*html*/ `
            <a target="${item.data.openInNewTab ? '_blank' : '_self'}" href="${item.data.urlConstruct}" class="news-item">
              <img src="${item.data.cardImage.iv[0].url}" class="news-item-image"/>
              <tj-box display="flex" justifyContent="space-between" mt="8px" mb="4px" height="auto">
                <div class="news-date">${this.formatDate(item.data.publishDate?.iv)}</div>
              </tj-box>
              <h3 class="line-clamp-2">${item.data.title[this.lang]}</h3>
            </a>
          `
        )
        .join('')}
    </div>
    ${this.renderPagination()}
  `;

  css = () => `
    .page-title {
      margin-bottom: 48px;
    }
    .line-clamp-2 {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .news-item-image {
      width: 100%;
      object-fit: cover;
      aspect-ratio: 16/9;
      border-radius: 16px;ß
    }
    .news-item {
      transition: ease all 0.2s;
      text-decoration: none;
      font-family: inherit;
      width: 100%;
    }
    .news-item:hover {
      transform: scale(1.03)
    }
    .news-date {
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      text-transform: uppercase;
      color: #133762;
    }
    .news-item h3 {
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
      color: #133762;
      margin: 0;
    }
    .news-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      row-gap: 40px;
      column-gap: 24px;
    }
    .pagination-button {
      font-weight: 700;
      font-size: 14px;
      line-height: 150%;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      text-transform: uppercase;
      color: #133762;
      border-radius: 8px;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
    }
    .pagination-button.active {
      background: #068FC3;
      color: #FFF;
    }
    .pagination {
      display: flex;
      justify-content: center;
    }
    tj-pagination-prev-icon, tj-pagination-next-icon {
      width: 24px;
      height: 24px;
    }
    .pagination {
      margin-top: 24px;
    }
    @media (max-width: 768px) {
      .news-grid {
        grid-template-columns: 1fr;
        row-gap: 32px;
        column-gap: 32px;
      }
      .news-item {
        width: 100%;
      }
      .page-title {
        width: 100%;
        text-align: center;
        margin-bottom: 32px;
      }
    }
  `;

  eventListeners: EventListeners = [['.pagination-button', 'click', this.changePage]];
}
